import EditIcon from "@mui/icons-material/Edit";
import { Divider, Grid, Tooltip, useTheme } from "@mui/material";
import * as _ from "lodash";
import React, { CSSProperties, ReactElement, ReactNode, useState } from "react";
import Select, { ActionMeta, components } from "react-select";
import { isArrayType, isNullableType } from "utils/TypeGuards";
import { v4 as uuidv4 } from "uuid";
import { default as hexToRGB } from "utils/hexToRGB";
import theme from "../../../../Theme/AppTheme";
import AcxInputLabel from "../../AcxInputLabel";
import DownChevronSvg from "./DownChevronSvg";
import { styleClsx, sxClsx } from "utils/styleClsx";

export type DisabledAppearance = "default" | "readOnly";

interface IAcxSelect {
    containerOffset?: { left?: string; right?: string };
    options: any[];
    id: string;
    onChange?: (arg0: any | undefined) => void;
    onMultiChange?: (arg0: any[] | undefined) => void;
    placeholder?: string;
    isMulti?: boolean;
    menuPortalTarget?: HTMLElement | null | undefined;
    defaultValue?: any;
    valueField?: string;
    labelField?: string;
    helpField?: string;
    colorField?: string;
    inputLabel?: React.ReactNode;
    noValue?: boolean;
    enableNoSelection?: boolean;
    editIcon?: boolean;
    onEditClick?: () => void;
    onBlur?: () => void;
    onMenuOpen?: () => void;
    onClick?: () => void;
    isClearable?: boolean;
    isLoading?: boolean;
    shrink?: boolean;
    infoText?: React.ReactNode;
    fullWidth?: boolean;
    minWidth?: string;
    width?: string;
    containerHeight?: string | "auto";
    maxContainerHeight?: string;
    alignControls?: CSSProperties["alignItems"];
    isDisabled?: boolean;
    /**
     * Controls only the visual treatment of a disabled select. `readOnly`
     * keeps the value at full contrast with a transparent background and the
     * normal border, and hides the dropdown indicator; the select remains
     * semantically and functionally disabled.
     */
    disabledAppearance?: DisabledAppearance;
    menuPlacement?: "top" | "bottom" | "auto";
    customStyle?: Record<string, (provided, state) => CSSProperties>;
    extendedCustomStyle?: Record<string, (provided, state) => CSSProperties>;
    // customRootStyles?: React.CSSProperties;
    maxMenuHeightPx?: number;
    /**
     * Minimum height the menu must have room for before "auto" placement gives
     * up on rendering below and flips above. Defaults to react-select's 140px,
     * which lets the menu render downward and squash itself when space is
     * tight. Set this equal to maxMenuHeightPx for fit-or-flip behavior.
     */
    minMenuHeightPx?: number;
    required?: boolean;
    customFilter?: (option, rawInput: string) => boolean;
    groupCollapse?: boolean;
    skeleton?: boolean;
    helperText?: React.ReactNode;
    showAllErrors?: boolean;
    error?: boolean;
    optionComponent?: (props) => ReactNode;
    tooltips?: { [key: string]: string };
    dropdownMenuEndChildren?: React.ReactNode;
    alternateItemColor?: string;
    dropdownIndicator?: React.ReactNode;
    isSearchable?: boolean;
    labelIcon?: React.ReactNode;
    components?: React.ComponentProps<typeof Select>["components"];
    disabledOptionTooltipText?: string;
    labelSx?: React.CSSProperties;
    isOptionDisabled?: (option: any) => boolean;
    getOptionTooltip?: (option: any) => string | undefined;
}

export interface OptionType {
    label: string;
    value: string;
}

function AcxSelect(incomingProps: IAcxSelect) {
    const props: IAcxSelect = {
        isMulti: false,
        enableNoSelection: false,
        editIcon: false,
        isClearable: false,
        isLoading: false,
        shrink: false,
        required: false,
        ...incomingProps,
    };
    const valueField = props.valueField ?? "value";
    const labelField = props.labelField ?? "label";
    const hasReadOnlyDisabledAppearance =
        props.isDisabled && props.disabledAppearance === "readOnly";
    const appTheme = useTheme();
    const [labelFocus, setLabelFocus] = useState<boolean | undefined>();
    const [currentValue, setCurrentValue] = useState<any>(props.defaultValue);
    const [options, setOptions] = useState<any[]>(props.options);
    const [thisId, setThisId] = useState<string>();
    const [shouldCollapse, setShouldCollapse] = useState<boolean>(
        props.groupCollapse ?? false,
    );

    const onEditClick = (e: React.MouseEvent) => {
        e.stopPropagation();
        e.preventDefault();

        if (props.onEditClick) {
            props.onEditClick();
        }
    };

    const IndicatorSeparator = ({ innerProps }) => {
        if (props.editIcon) {
            return (
                <Grid
                    onMouseDown={onEditClick}
                    sx={{
                        padding: "6px",
                        cursor: "pointer",
                        "&:hover": {
                            color: hexToRGB(theme.palette.black.main, 0.6),
                        },
                    }}
                >
                    <EditIcon viewBox={"0 0 20 20"} style={{ fontSize: 14 }} />
                </Grid>
            );
        } else {
            return <div></div>;
        }
    };

    const onHeaderClick = (id) => {
        const node =
            document.getElementById(id)?.parentElement?.nextElementSibling;
        if (node) {
            for (let i = 0; i < node.children.length; i++) {
                const element = node.children[i] as HTMLElement;
                const styles = element.style;
                if (styles.display === "none") {
                    styles.display = "inline";
                } else {
                    styles.display = "none";
                }
            }
        }
    };

    const GroupHeading = (p) => {
        return (
            <div
                className={`grp-${thisId}`}
                style={{
                    color: "white",
                    paddingTop: theme.spacing(0.5),
                    paddingBottom: theme.spacing(0.5),
                    paddingLeft: theme.spacing(0.5),
                }}
                onClick={() => onHeaderClick(p.id)}
            >
                <components.GroupHeading {...p} />
            </div>
        );
    };

    const onFilter = (option, rawInput: string) => {
        if (props.groupCollapse) {
            if (rawInput?.length > 0) {
                setShouldCollapse(false);
                const tgt = document.querySelector(`div.grp-${thisId}`);
                const node = tgt?.nextElementSibling;

                if (node) {
                    for (let i = 0; i < node.children.length; i++) {
                        const element = node.children[i] as HTMLElement;
                        const styles = element.style;
                        if (styles.display === "none") {
                            styles.display = "inline";
                        }
                    }
                }
            } else {
                setShouldCollapse(true);
            }
        }

        if (rawInput === "") {
            return true;
        }

        let parent: string = option.data.parent ?? option.data;
        if (!parent) return false;

        const compare: string = rawInput.toLowerCase();

        if (parent && parent["value"]) {
            parent = parent["value"];
        }

        if (
            (parent?.toLowerCase && parent?.toLowerCase().includes(compare)) ||
            (option?.label?.toLowerCase &&
                option.label.toLowerCase().includes(compare)) ||
            (option?.data?.name?.toLowerCase &&
                option.data?.name?.toLowerCase().includes(compare))
        ) {
            return true;
        }

        return false;
    };

    const multiValueOption = (p) => {
        const tooltipTitle = p.selectProps.getOptionTooltip(p.data) ?? "";

        return (
            <Tooltip title={tooltipTitle}>
                <div
                    style={{
                        width: "100%",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                    }}
                >
                    <components.MultiValueLabel {...p} />
                </div>
            </Tooltip>
        );
    };

    const singleValueOption = (p) => {
        const tooltipTitle = p.selectProps.getOptionTooltip(p.data) ?? "";

        return (
            <div>
                <Tooltip title={tooltipTitle}>
                    <div>
                        <components.SingleValue {...p} />
                    </div>
                </Tooltip>
            </div>
        );
    };

    const Option = (p) => {
        if (props.optionComponent) {
            return props.optionComponent(p) as ReactElement;
        }

        const { children } = p;

        return (
            <div style={styleClsx(shouldCollapse && { display: "none" })}>
                <Tooltip
                    title={
                        // Currently, the only place the isDisabled prop is used for select items in in convo data labelling clip selection
                        // May need to refactor this into a reusable prop instead
                        p.isDisabled ? props.disabledOptionTooltipText : ""
                    }
                    placement="left"
                    arrow
                >
                    <div>
                        <components.Option
                            {...p}
                            innerProps={{
                                ...p.innerProps, // keep default handlers like onMouseMove, onClick, etc.
                                id: p.data.id || p.innerProps?.id, // p.data.id is the custom id passed in on the option
                            }}
                        >
                            {children}
                        </components.Option>
                    </div>
                </Tooltip>
            </div>
        );
    };

    const customStyles = {
        option: (provided, state) => ({
            ...provided,
            color:
                state.isSelected || state.isFocused
                    ? state.isFocused
                        ? theme.palette.primary.main
                        : theme.palette.primary.dark
                    : props.alternateItemColor ?? theme.palette.text.primary,
            backgroundColor:
                state.isSelected || state.isFocused
                    ? theme.palette.primary[50]
                    : theme.palette.white.main,
            "&:hover": !state.isDisabled
                ? {
                      backgroundColor: theme.palette.primary[50],
                      color: theme.palette.neutral[600],
                  }
                : {},
            opacity: state.isDisabled ? "50%" : "100%",
            padding: appTheme.spacing(1),
            borderRadius: "2px",
        }),
        groupHeading: (provided, state) => ({
            // ...provided,
            color: theme.palette.gray[700],
            font: appTheme.typography.fontFamily,
            fontSize: "13px",
            cursor: "pointer",
            fontWeight: "bold",
        }),
        menu: (provided, state) => ({
            ...provided,
            // width: "300px",
            zIndex: 10,
            padding: "8px",
        }),
        menuPortal: (provided) => ({
            ...provided,
            zIndex: 9999,
        }),
        control: () => ({
            display: "flex",
            cursor: "pointer",
        }),
        singleValue: (provided, state) => {
            if (props.colorField) {
                const fontWeight = "bold";
                const fontSize = "12px";
                const lineHeight = "15px";
                const paddingLeft = "6px";
                const paddingRight = "6px";
                const paddingTop = "3px";
                const paddingBottom = "3px";
                const backgroundColor = hexToRGB(
                    state.data[props.colorField ?? ""] ??
                        appTheme.palette.info.main,
                    0.18,
                );
                const color =
                    state.data[props.colorField ?? ""] ??
                    appTheme.palette.info.main;
                return {
                    ...provided,
                    backgroundColor,
                    color,
                    fontWeight,
                    paddingTop,
                    paddingRight,
                    paddingBottom,
                    paddingLeft,
                    fontSize,
                    lineHeight,
                };
            }
            const opacity =
                state.isDisabled && !hasReadOnlyDisabledAppearance ? 0.5 : 1;
            const transition = "opacity 300ms";
            return {
                ...provided,
                color: hasReadOnlyDisabledAppearance
                    ? appTheme.palette.text.primary
                    : provided.color,
                opacity,
                transition,
            };
        },
        dropdownIndicator: (provided, state) => ({
            ...provided,
            color: appTheme.palette.text.primary,
            display: hasReadOnlyDisabledAppearance ? "none" : provided.display,
            padding: "6px !important",
        }),
        clearIndicator: (provided, state) => ({
            ...provided,
            padding: "6px !important",
        }),
        indicatorsContainer: (provided, state) => ({
            ...provided,
            alignItems: props.alignControls ?? "center",
            height: "fit-content",
            position: "sticky",
            top: 0,
        }),
        multiValue: (styles, { data }) => ({
            ...styles,
            backgroundColor: hexToRGB(
                data[props.colorField ?? ""] ?? appTheme.palette.info.main,
                0.18,
            ),
            // border: `2px dotted ${appTheme.palette.secondary.main}`,
            fontSize: "13px",
            color: data[props.colorField ?? ""] ?? appTheme.palette.info.main,
        }),
        multiValueLabel: (styles, { data }) => ({
            ...styles,
            color: data[props.colorField ?? ""] ?? appTheme.palette.info.main,
            fontWeight: "bold",
        }),
        container: (provided, state) => ({
            ...provided,
            fontSize: "13px",
            minWidth: props.minWidth ?? "100px",
            width: props.width ?? "100%",
            maxWidth: props.fullWidth ? "100%" : "336px",
            border:
                props.isDisabled && !hasReadOnlyDisabledAppearance
                    ? "none"
                    : "1px solid",
            borderColor: state.isFocused
                ? hexToRGB(theme.palette.primary.main, 0.7)
                : appTheme.palette.lightgrayBorder.main,
            borderRadius: appTheme.shape.borderRadius,
            minHeight: props.containerHeight ?? "32px",
            height: props.containerHeight ?? "32px",
            maxHeight: props.maxContainerHeight,
            overflowY: !isNullableType(props.maxContainerHeight)
                ? "auto"
                : "none",
            backgroundColor: hasReadOnlyDisabledAppearance
                ? "transparent"
                : props.isDisabled
                  ? hexToRGB(appTheme.palette.black.main, 0.1)
                  : appTheme.palette.white.main,
        }),
    };
    _.assign(customStyles, props.customStyle ?? {});

    const onChange = (value: any, actionMeta: ActionMeta<any>) => {
        if (value?.[valueField] === "-1" || actionMeta.action === "clear") {
            setCurrentValue(null);
            value = undefined;
        } else {
            setCurrentValue(value);
        }
        if (value) {
            if (isArrayType(value)) {
                if (props.onMultiChange) {
                    props.onMultiChange(value);
                }
            } else {
                if (props.onChange) {
                    props.onChange(value);
                }
            }
        } else {
            if (props.onChange) {
                props.onChange(undefined);
            }
            if (props.onMultiChange) {
                props.onMultiChange(undefined);
            }
        }
    };

    React.useEffect(() => {
        setThisId(uuidv4());
    }, []);

    React.useEffect(() => {
        if (props.enableNoSelection) {
            if (props.options.length > 0) {
                const curops = [
                    {
                        [valueField]: "-1",
                        [labelField]: "No Selection",
                    },
                ];
                setOptions(curops.concat(props.options));
            } else {
                const curops = [
                    {
                        [valueField]: "-1",
                        [labelField]: "No Selection",
                    },
                ];
                setOptions(curops);
            }
        } else {
            setOptions(props.options);
        }
    }, [props.options, props.enableNoSelection, valueField, labelField]);

    React.useEffect(() => {
        if (props.defaultValue === undefined || props.defaultValue === null) {
            setCurrentValue(null);
        } else {
            if (isArrayType(props.defaultValue)) {
                setCurrentValue(props.defaultValue);
            } else {
                setCurrentValue(props.defaultValue);
            }
        }
    }, [props.defaultValue, props.options, valueField]);

    const DropDownIndicator = (inheritedProps) => {
        return (
            <components.DropdownIndicator {...inheritedProps}>
                {props.dropdownIndicator ? (
                    props.dropdownIndicator
                ) : (
                    <DownChevronSvg />
                )}
            </components.DropdownIndicator>
        );
    };

    const Menu = (menuProps) => {
        return (
            <>
                <components.Menu {...menuProps}>
                    {menuProps.children}
                    {props.dropdownMenuEndChildren &&
                        React.Children.count(props.dropdownMenuEndChildren) >
                            0 && (
                            <>
                                <Divider />
                                {props.dropdownMenuEndChildren}
                            </>
                        )}
                </components.Menu>
            </>
        );
    };

    function getCustomComponents() {
        return {
            IndicatorSeparator: IndicatorSeparator,
            GroupHeading: GroupHeading,
            Option: Option,
            MultiValueLabel: multiValueOption,
            SingleValue: singleValueOption,
            DropdownIndicator: DropDownIndicator,
            Menu,
            ...props.components,
        };
    }

    return (
        <>
            {props.inputLabel && (
                <AcxInputLabel
                    shrink={props.shrink}
                    focused={labelFocus}
                    id={props.id + "-label"}
                    htmlFor={props.id}
                    infoText={props.infoText}
                    sx={sxClsx(
                        {
                            fontFamily: theme.typography.fontFamily,
                            color: theme.palette.text.primary,
                            fontWeight: theme.typography.fontWeightRegular,
                            fontSize: "12px",
                            lineHeight: "16px",
                        },
                        props.labelIcon != null && {
                            display: "flex",
                            alignItems: "center",
                        },
                        props.required &&
                            !currentValue && {
                                "&:after": {
                                    color: theme.palette.red.main,
                                    content: `" *"`,
                                },
                            },
                        props.labelSx,
                    )}
                >
                    <span
                        style={
                            props.labelIcon
                                ? { paddingRight: theme.spacing(1) }
                                : undefined
                        }
                    >
                        {props.inputLabel}
                    </span>
                    {props.labelIcon}
                </AcxInputLabel>
            )}

            <Select
                id={props.id}
                className="pendo-ignore"
                isLoading={props.isLoading}
                isClearable={props.isClearable}
                maxMenuHeight={props.maxMenuHeightPx}
                minMenuHeight={props.minMenuHeightPx}
                width={"100%"}
                aria-labelledby={props.id + "-label"}
                onChange={onChange}
                onBlur={() => {
                    setLabelFocus(false);
                    props.onBlur?.();
                }}
                isSearchable={props.isSearchable}
                onFocus={() => setLabelFocus(true)}
                onMenuOpen={props.onMenuOpen}
                onClick={props.onClick}
                placeholder={props.placeholder ?? "Search..."}
                defaultValue={props.noValue ? null : currentValue}
                value={props.noValue ? null : currentValue}
                styles={customStyles}
                options={options}
                menuShouldScrollIntoView
                isMulti={props.isMulti}
                menuPortalTarget={
                    props.menuPortalTarget === undefined
                        ? document.body
                        : props.menuPortalTarget
                } // null is significant
                menuPlacement={props.menuPlacement ?? "auto"}
                inputId={props.id}
                sx={sxClsx(
                    {
                        width: "100%",
                        backgroundColor: theme.palette.white.main,
                        border: `1px solid ${theme.palette.lightgrayBorder.main}`,
                    },
                    props.error && {
                        borderWidth: "2px !important",
                        borderColor: `${theme.palette.error.main} !important`,
                    },
                )}
                getOptionValue={(option) => option[valueField]}
                getOptionLabel={(option) => option[labelField]}
                // gets values for custom tooltips for individual inputs in the multi select input
                getOptionTooltip={(option) =>
                    props.getOptionTooltip?.(option) ??
                    props.tooltips?.[option[labelField]]
                }
                components={getCustomComponents()}
                isDisabled={props.isDisabled}
                isOptionDisabled={props.isOptionDisabled}
                filterOption={props.customFilter ?? onFilter ?? undefined}
            />

            {props.helperText !== undefined && (
                <AcxInputLabel
                    id={"AcxMainText-BottomLabel"}
                    sx={sxClsx(
                        {
                            fontFamily: theme.typography.fontFamily,
                            color: theme.palette.text.primary,
                            fontSize: "12px",
                            lineHeight: "16px",
                        },
                        props.error && {
                            color: `${theme.palette.error.main} !important`,
                        },
                    )}
                    showAllErrors={!!props.showAllErrors}
                >
                    {props.helperText}
                </AcxInputLabel>
            )}
        </>
    );
}

export default React.memo(AcxSelect);
